import { useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";

export function useExport(tableName, columns, tableId) {
    const { call: logExportAuditCall } = useBluelagoon({ method: "logExportAudit" });

    const logAudit = useCallback((format, recordCount) => {
        logExportAuditCall({
            resourceType: "table",
            resourceId: tableId || "",
            resourceName: tableName,
            exportFormat: format,
            recordCount,
        }).then(() => {
            toast.success("Export audited");
        }).catch(() => {});
    }, [logExportAuditCall, tableId, tableName]);

    const downloadFile = useCallback((content, filename, mimeType) => {
        const blob = new Blob([content], { type: mimeType });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Exported to ${filename}`);
    }, []);

    const exportCSV = useCallback((records) => {
        const cols = columns.map((c) => c.name);
        const header = cols.join(",");
        const rows = records.map((r) =>
            cols.map((c) => {
                const val = r[c];
                if (val == null) return "";
                const str = typeof val === "object" ? JSON.stringify(val) : String(val);
                return str.includes(",") || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str;
            }).join(",")
        );
        downloadFile([header, ...rows].join("\n"), `${tableName}.csv`, "text/csv");
        logAudit("csv", records.length);
    }, [columns, tableName, downloadFile, logAudit]);

    const exportJSON = useCallback((records) => {
        downloadFile(JSON.stringify(records, null, 2), `${tableName}.json`, "application/json");
        logAudit("json", records.length);
    }, [tableName, downloadFile, logAudit]);

    const exportSQL = useCallback((records) => {
        const cols = columns.filter((c) => c.name !== "id").map((c) => c.name);
        const sql = records.map((r) => {
            const vals = cols.map((c) => {
                const v = r[c];
                if (v == null) return "NULL";
                if (typeof v === "number") return v;
                return `'${String(v).replace(/'/g, "''")}'`;
            });
            return `INSERT INTO ${tableName} (${cols.join(", ")}) VALUES (${vals.join(", ")});`;
        }).join("\n");
        downloadFile(sql, `${tableName}.sql`, "text/plain");
        logAudit("sql", records.length);
    }, [columns, tableName, downloadFile, logAudit]);

    return { exportCSV, exportJSON, exportSQL };
}
